/**
 * Choosing a face, out of a list that is no longer twelve long.
 *
 * It used to open inline, under the field, as one flat grid. That was fine for
 * twelve emoji and became unusable at seventy-two: the grid pushed the form
 * off the bottom of the phone, so choosing a face meant losing sight of the
 * name you had just typed and the button you were heading for.
 *
 * A sheet instead — the same one the table menu and the rules use. It covers
 * the form rather than moving it, and closing it puts you back exactly where
 * you were.
 *
 * ## Grouped, because the groups are the product
 *
 * The server sends the faces this account may wear, already divided: the ones
 * everybody has, then a group per theme owned. That division is not decoration
 * — a face from a theme is a thing somebody bought, and a wall of
 * seventy-two undifferentiated emoji would hide the ones they paid for among
 * the ones they did not.
 *
 * Which groups you see is the server's answer, never a filter applied here.
 * A picker that offered a face the server would refuse is the shape of bug
 * that kept the wardrobe empty until the server could answer for it.
 */

import { Icon } from '../render/Icon.js';
import { avatarEmoji } from '../render/avatars.js';
import { useSession } from '../session-context.js';

export function AvatarSheet({
  chosen,
  onPick,
  onClose,
}: {
  chosen: string | null;
  onPick: (avatarId: string) => void;
  onClose: () => void;
}) {
  const { config } = useSession();
  const groups = config?.avatarGroups ?? [];
  const labels = new Map((config?.avatars ?? []).map((avatar) => [avatar.id, avatar.label]));

  return (
    <div
      className="sheet sheet-solid"
      role="dialog"
      aria-modal="true"
      aria-label="Your face"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet-body">
        <div className="sheet-head">
          <p className="sheet-title">Your face</p>
          <button type="button" className="sheet-close" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>

        {groups.map((group) => (
          <div className="menu-section" key={group.name}>
            <p className="menu-section-title">{group.name}</p>
            <div className="avatar-grid">
              {group.avatarIds.map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`avatar-choice${id === chosen ? ' chosen' : ''}`}
                  aria-label={labels.get(id) ?? id}
                  aria-pressed={id === chosen}
                  onClick={() => {
                    onPick(id);
                    /* Picked is picked. Leaving it open would hide the choice
                       just made behind the grid it was made from. */
                    onClose();
                  }}
                >
                  <span aria-hidden="true">{avatarEmoji(id)}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
